/**
 * Project-level database operations.
 *
 * These go beyond individual task ops — they handle bulk operations
 * on a project/list and all its children (complete, delete, duplicate,
 * convert type).
 */

import { db, ObjectType, ObjectStatus } from "@/db/schema"
import type { DbObject } from "@/db/schema"
import { createTaskDefaults } from "@/shared/types/task"
import { now } from "@/shared/utils/date/dateUtils"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get all non-deleted children of a project/list */
async function getChildren(projectId: string): Promise<DbObject[]> {
  const parentPath = `root/${projectId}`
  return db.totodo
    .where("[parentPath+isDeleted]")
    .equals([parentPath, 0])
    .toArray()
}

// ---------------------------------------------------------------------------
// Complete / cancel / reopen a project or list
// ---------------------------------------------------------------------------

// Lives in @totodo/operations (closeContainer / reopenContainer, shared with REST and MCP) and is
// reached from the app through ./projectStatusRequest (setProjectStatus), which asks what happens
// to the open to-dos before it closes a project that still has some.

// ---------------------------------------------------------------------------
// Archive section (complete/uncomplete with optional children)
// ---------------------------------------------------------------------------

/**
 * Archive or restore a section.
 *
 * When archiving with children, all unfinished tasks under this section
 * (positionally: between this section and the next section, same parentPath)
 * are also completed.
 *
 * The section gets `statusAt = now + 1000ms` so it sorts above its children
 * in the Logbook/Logged Items view.
 *
 * @param sectionId - The section ID
 * @param includeChildren - If true, also complete unfinished children
 * @param childIds - Pre-computed child IDs to include (avoids duplicate positional lookup)
 */
export async function archiveSection(
  sectionId: string,
  includeChildren: boolean,
  childIds?: string[],
): Promise<void> {
  const timestamp = now()

  await db.transaction("rw", db.totodo, async () => {
    const section = await db.totodo.get(sectionId)
    if (!section) return

    const isArchived =
      section.status === ObjectStatus.Completed ||
      section.status === ObjectStatus.Canceled

    if (isArchived) {
      // Restore — just uncomplete the section
      await db.totodo.update(sectionId, {
        status: ObjectStatus.Unfinished,
        statusAt: timestamp,
        updatedAt: timestamp,
      })
      return
    }

    // Archive the section — statusAt +1s so it sorts above children in Logbook
    await db.totodo.update(sectionId, {
      status: ObjectStatus.Completed,
      statusAt: timestamp + 1000,
      updatedAt: timestamp,
    })

    if (includeChildren && childIds && childIds.length > 0) {
      // Bulk-complete all unfinished children
      const children = await db.totodo.bulkGet(childIds)
      const updates = children
        .filter(
          (c): c is DbObject =>
            c !== undefined && c.status === ObjectStatus.Unfinished,
        )
        .map((c) => ({
          key: c.id as string,
          changes: {
            status: ObjectStatus.Completed as const,
            statusAt: timestamp,
            updatedAt: timestamp,
          },
        }))

      if (updates.length > 0) {
        await db.totodo.bulkUpdate(updates)
      }
    }
  })
}

// ---------------------------------------------------------------------------
// Delete section / project (soft-delete) — shared with the server (delete_container)
// ---------------------------------------------------------------------------

// The cascade of a delete lives in @totodo/operations: the app and the server's delete_container
// call the same functions, so a container deleted over MCP is deleted exactly as the sidebar does it.
export { deleteSection, deleteProjectWithChildren } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Duplicate project + all children
// ---------------------------------------------------------------------------

/**
 * Duplicate a project/list and all its children.
 * @returns The new project's ID.
 */
export async function duplicateProject(
  projectId: string,
): Promise<string | null> {
  const original = await db.totodo.get(projectId)
  if (!original) return null

  const timestamp = now()
  const newProjectId = crypto.randomUUID()

  // Create the new project (same type, copy metadata)
  const newProject = createTaskDefaults({
    id: newProjectId,
    type: original.type,
    title: `${original.title} (copy)`,
    parentPath: original.parentPath,
    index: original.index + 0.001, // Place right after original
    status: ObjectStatus.Unfinished,
    tags: [...original.tags],
    dueAt: original.dueAt,
    createdAt: timestamp,
    updatedAt: timestamp,
    owner: original.owner,
    realmId: original.realmId,
  })

  // Duplicate children
  const children = await getChildren(projectId)
  const newChildren = children.map((child) => {
    const newChildId = crypto.randomUUID()
    return createTaskDefaults({
      id: newChildId,
      type: child.type,
      title: child.title,
      note: child.note,
      parentPath: `root/${newProjectId}`,
      index: child.index,
      status: ObjectStatus.Unfinished,
      tags: [...child.tags],
      dueAt: child.dueAt,
      createdAt: timestamp,
      updatedAt: timestamp,
      owner: child.owner,
      realmId: child.realmId,
    })
  })

  await db.transaction("rw", db.totodo, async () => {
    await db.totodo.add(newProject)
    if (newChildren.length > 0) {
      await db.totodo.bulkAdd(newChildren)
    }
  })

  return newProjectId
}

// ---------------------------------------------------------------------------
// Convert project ↔ list
// ---------------------------------------------------------------------------

/**
 * Toggle a project to a list, or a list to a project.
 */
export async function convertProjectType(projectId: string): Promise<void> {
  const project = await db.totodo.get(projectId)
  if (!project) return

  const newType =
    project.type === ObjectType.Project ? ObjectType.List : ObjectType.Project

  await db.totodo.update(projectId, {
    type: newType,
    updatedAt: now(),
  })
}
